// src/pages/import/declaracoes-aduaneiras/baixa/DeclaracaoAduaneiraBaixaConsultPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import {
    Grid, MenuItem, Select, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip, Menu
} from '@material-ui/core';
import { MoreHoriz as MoreHorizIcon } from '@material-ui/icons';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import QCXPopupState from '../../../../components/popup-state/QCXPopupState';

import { useTranslation } from 'react-i18next';
import SimpleConsultPageTemplate from '../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';

import {
    fetchBaixaDAMercadoriasByFilterAsync,
    fetchFollowupsDestinoComDiAsync,
    transferirItensDaAsync,
    salvarQtdeBaixaDaAsync,
} from '../../../../features/declaracoesAduaneiras/declaracoesAduaneirasThunks';

import {
    selectBaixaDAMercadoriasList,
    selectBaixaDAMercadoriasCount,
    selectBaixaDAMercadoriasStatus,
    selectFollowupsDestinoList,
    selectFollowupsDestinoStatus,
} from '../../../../features/declaracoesAduaneiras/declaracoesAduaneirasSlice';

import { isLoadingStatus } from '../../../../utils/store/store-utils';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import QCXTextField from '../../../../shared-components/text-field/QCXTextField';
import { focusLockActions } from '../../../../features/focus-lock/focusLockSlice'; // << FIX
import { Form, FormSpy } from 'react-final-form';
import QCXSelectFollowUpDestinoAutocompleteField from '../../../../shared-components/select-followup-destino-baixa/QCXSelectFollowUpDestinoAutocompleteField';
import QCXInfoAlert from '../../../../shared-components/alert/QCXInfoAlert';

export default function DeclaracaoAduaneiraBaixaConsultPage({ authInfo = {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const { id: followupIdOrigem } = useParams();
    const dispatch = useDispatch();

    const list = useSelector(selectBaixaDAMercadoriasList);
    const count = useSelector(selectBaixaDAMercadoriasCount);
    const status = useSelector(selectBaixaDAMercadoriasStatus);
    const isLoading = useMemo(() => isLoadingStatus(status), [status]);

    const followupsDestinoStatus = useSelector(selectFollowupsDestinoStatus);

    const [lastPage, setLastPage] = useState(1);
    const [lastSize, setLastSize] = useState(10);
    const [lastFilter, setLastFilter] = useState('');

    const [baixarMode, setBaixarMode] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);

    const [qtdeMap, setQtdeMap] = useState({});
    const [followUpDestinoId, setFollowUpDestinoId] = useState('');

    const [dlgQtdeOpen, setDlgQtdeOpen] = useState(false);
    const [rowCtx, setRowCtx] = useState(null);
    const [qtdeInput, setQtdeInput] = useState('');

    const handleFocusInput = useCallback(() => {
        dispatch(focusLockActions.unlock());
    }, [dispatch]);

    const handleBlurInput = useCallback(() => {
        dispatch(focusLockActions.lock());
    }, [dispatch]);

    const fmt = (n) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 }).format(Number(n || 0));

    const fetchGrid = useCallback(({ currentPage, pageSize, filterInputValue }) => {
        setLastPage(currentPage);
        setLastSize(pageSize);
        setLastFilter(filterInputValue || '');
        dispatch(
            fetchBaixaDAMercadoriasByFilterAsync([
                { name: 'page', value: currentPage },
                { name: 'size', value: pageSize },
                { name: 'followupId', value: followupIdOrigem },
                { name: 'search', value: filterInputValue },
            ])
        );
    }, [dispatch, followupIdOrigem]);

    const refreshGrid = useCallback(() => {
        fetchGrid({ currentPage: lastPage, pageSize: lastSize, filterInputValue: lastFilter });
    }, [fetchGrid, lastPage, lastSize, lastFilter]);

    useEffect(() => {
        fetchGrid({ currentPage: 1, pageSize: 10, filterInputValue: '' });
    }, [fetchGrid]);

    useEffect(() => {
        dispatch(fetchFollowupsDestinoComDiAsync([{ name: 'origemFollowupId', value: followupIdOrigem }]));
    }, [dispatch, followupIdOrigem]);

    const handleConfigChange = useCallback(({ currentPage, pageSize, filterInputValue }) => {
        fetchGrid({ currentPage, pageSize, filterInputValue });
    }, [fetchGrid]);

    const openQtdeDialog = useCallback((row) => {
        setRowCtx(row);
        setQtdeInput(''); 
        setDlgQtdeOpen(true);
    }, []);

    const closeQtdeDialog = useCallback(() => {
        setDlgQtdeOpen(false);
        setRowCtx(null);
        setQtdeInput('');
    }, []);

    const saldoRow = useMemo(() => {
        if (!rowCtx) return 0;
        return Number(rowCtx?.quantidade || 0) - Number(rowCtx?.quantidadeBaixada || 0);
    }, [rowCtx]);

    const qtdeInputNumber = useMemo(() => {
        const n = Number(String(qtdeInput).replace(',', '.'));
        return Number.isNaN(n) ? 0 : n;
    }, [qtdeInput]);

    const qtdeValida = useMemo(() => qtdeInputNumber >= 0 && qtdeInputNumber <= saldoRow, [qtdeInputNumber, saldoRow]);

    const handleSalvarQtde = useCallback(async () => {
        if (!rowCtx?.id) { closeQtdeDialog(); return; }
        if (!qtdeValida) {
            dispatch(setErrorFeedback({ message: 'Quantidade inválida (maior que o saldo ou negativa).' }));
            return;
        }

        const res = await dispatch(
            salvarQtdeBaixaDaAsync({ mercadoriaId: Number(rowCtx.id), quantidade: qtdeInputNumber })
        );

        if (salvarQtdeBaixaDaAsync.fulfilled.match(res)) {
            closeQtdeDialog();
            refreshGrid();
        }
    }, [dispatch, rowCtx, qtdeInputNumber, qtdeValida, refreshGrid, closeQtdeDialog]);

    const columns = useMemo(() => [
        { field: 'ad', headerName: 'Ad', align: 'center', headerAlign: 'center', width: 80, valueGetter: ({ row }) => row?.ad || '-' },
        { field: 'it', headerName: 'It', align: 'center', headerAlign: 'center', width: 80, valueGetter: ({ row }) => row?.item || row?.it || '-' },
        { field: 'pn', headerName: 'Pn', flex: 140, valueGetter: ({ row }) => row?.partNumber || row?.pn || '-' },
        { field: 'desc', headerName: 'Desc.', flex: 260, valueGetter: ({ row }) => row?.descricao || '-' },
        { field: 'qtde', headerName: 'Qtde', align: 'right', width: 110, valueGetter: ({ row }) => fmt(row?.quantidade) },
        { field: 'baixados', headerName: 'Baixados', align: 'right', width: 120, valueGetter: ({ row }) => fmt(row?.quantidadeBaixada) },
        { field: 'qtdeBaixar', headerName: 'Qtde a Baixar', align: 'right', width: 130, valueGetter: ({ row }) => fmt(row?.quantidadeABaixar) },
        {
            field: 'saldo',
            headerName: 'Saldo',
            align: 'right',
            width: 110,
            valueGetter: ({ row }) => {
                const saldo = Number(row?.quantidade || 0) - Number(row?.quantidadeBaixada || 0);
                return fmt(saldo);
            },
        },
        {
            field: 'actions',
            headerName: t('com.muralis.qcx.acoes.label'),
            headerAlign: 'center',
            align: 'center',
            width: 80,
            sortable: false,
            renderCell: ({ row }) => (
                <QCXPopupState popupId={`popup-merc-${row.id}`}>
                    {(popupState) => (
                        <>
                            <Tooltip title={t('com.muralis.qcx.maisOpcoes')} arrow>
                                <IconButton size="small" {...bindTrigger(popupState)}>
                                    <MoreHorizIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Menu {...bindMenu(popupState)}>
                                <MenuItem
                                    onClick={(e) => {
                                        popupState.close(e);
                                        openQtdeDialog(row);
                                    }}
                                >
                                    <Typography variant="inherit" style={{ fontSize: 12 }}>
                                        INSERIR QTDE A BAIXAR
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </QCXPopupState>
            ),
        },
    ], [t, openQtdeDialog]);

    const canSubmit = useMemo(
        () => !!followUpDestinoId && Object.values(qtdeMap).some((v) => Number(v) > 0),
        [qtdeMap, followUpDestinoId]
    );

    const handleConfirmarBaixa = useCallback(async () => {
        if (!followUpDestinoId) {
            dispatch(setErrorFeedback({ message: 'Selecione o processo de destino.' }));
            return;
        }
        if (!selectedRowIds.length) {
            dispatch(setErrorFeedback({ message: 'Selecione ao menos um item com quantidade a baixar.' }));
            return;
        }

        const itens = selectedRowIds
            .map((id) => list.find((r) => `${r.id}` === `${id}`))
            .filter(Boolean)
            .map((row) => ({
                mercadoriaId: Number(row.id),
                quantidade: Number(row.quantidadeABaixar || row.quantidade_a_baixar || 0),
            }))
            .filter((i) => i.quantidade > 0);

        if (!itens.length) {
            dispatch(setErrorFeedback({ message: 'Nenhum item selecionado possui "Qtde a Baixar" > 0.' }));
            return;
        }

        const res = await dispatch(
            transferirItensDaAsync({
                followupOrigemId: Number(followupIdOrigem),
                followupDestinoId: Number(followUpDestinoId),
                itens,
            })
        );

        if (transferirItensDaAsync.fulfilled.match(res)) {
            dispatch(setSuccessFeedback({ message: 'Baixa realizada com sucesso.' }));
            setSelectedRowIds([]);
            setBaixarMode(false);
            fetchGrid({ currentPage: 1, pageSize: 10, filterInputValue: '' });
        }
    }, [dispatch, selectedRowIds, followUpDestinoId, followupIdOrigem, list, fetchGrid]);


    const breadcrumbs = [
        { link: { to: '/', name: t('com.muralis.qcx.inicio') } },
        { link: { to: t('com.muralis.qcx.url.moduloImportacao'), name: t('com.muralis.qcx.importacao.label') } },
        { link: { to: '/importacao/declaracoes-aduaneiras', name: 'Declarações Adicionais' } },
        { text: { name: 'Baixa de DA' }, default: true },
    ];

    const templateProps = useMemo(() => ({
        page: { title: 'Baixa de DA', icon: null, breadcrumbs },
        table: {
            columns,
            rowsCount: count,
            onConfigChangeCallback: handleConfigChange,
            checkboxSelection: baixarMode,
            isRowSelectable: (params) =>
                Number(params?.row?.quantidadeABaixar || params?.row?.quantidade_a_baixar || 0) > 0,
            onSelectionModelChange: (selectionModel) => {
                setSelectedRowIds(Array.isArray(selectionModel) ? selectionModel : selectionModel?.selectionModel || []);
            },
        },
    }), [columns, count, handleConfigChange, baixarMode]);

    const controlButtons = useMemo(() => {
        return baixarMode
            ? [
                {
                    description: 'Confirmar Baixa',
                    color: 'secondary',
                    onClick: () => handleConfirmarBaixa(),
                    disabled: selectedRowIds.length === 0 || !followUpDestinoId,
                },
                {
                    description: 'Cancelar',
                    variant: 'outlined',
                    onClick: () => {
                        setBaixarMode(false);
                        setSelectedRowIds([]);
                    },
                },
            ]
            : [
                {
                    description: 'SELECIONAR ITENS PARA BAIXA',
                    color: 'primary',
                    onClick: () => setBaixarMode(true),
                },
            ];
    }, [baixarMode, selectedRowIds.length, followUpDestinoId]);


    return (
        <>
            <SimpleConsultPageTemplate
                pageProps={templateProps.page}
                tableProps={templateProps.table}
                tableData={list}
                isLoading={isLoading}
                authInfo={authInfo}
                requiredRoles={['importacao-sda']}
                gridBelow
                controlProps={{ others: controlButtons }}   
            >
                <Form onSubmit={() => { }} initialValues={{ destino: { id: null } }}>
                    {() => (
                        <form>
                            <div
                                style={{ marginTop: 20, padding: '0 20px' }}
                                onKeyDownCapture={(e) => e.stopPropagation()}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4} lg={4}>
                                        <QCXSelectFollowUpDestinoAutocompleteField
                                            key="select-autocomplete-processo-destino-field"
                                            id="select-autocomplete-processo-destino-field"
                                            name="destino.id"
                                            label={t('com.muralis.qcx.numeroProcessoDestino')}
                                            origemFollowupId={Number(followupIdOrigem)}
                                            controlled={false}
                                            required
                                            onChange={(e, v) => setFollowUpDestinoId(v?.id || '')}
                                            onFocus={handleFocusInput}
                                            onBlur={handleBlurInput}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={8} lg={8} style={{ display: 'flex', alignItems: 'center' }}>

                                        <QCXInfoAlert>
                                            "Somente processos de DI/DUIMP NÃO LIBERADA e de mesmo importador são exibidos."
                                        </QCXInfoAlert>
                                    </Grid>
                                </Grid>

                                <FormSpy subscription={{ values: true }}>
                                    {({ values }) => {
                                        React.useEffect(() => {
                                            const id = values?.destino?.id;
                                            if (id && id !== followUpDestinoId) setFollowUpDestinoId(id);
                                        }, [values]);
                                        return null;
                                    }}
                                </FormSpy>
                            </div>
                        </form>
                    )}
                </Form>
            </SimpleConsultPageTemplate>

            <Dialog
                open={dlgQtdeOpen}
                onClose={closeQtdeDialog}
                maxWidth="xs"
                fullWidth
                keepMounted
                disableEnforceFocus   
                disableRestoreFocus   
            >
                <DialogTitle>Inserir quantidade a baixar</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom style={{ marginBottom: 10 }}>
                        {rowCtx ? `Saldo disponível: ${fmt(saldoRow)}` : ''}
                    </Typography>
                    <Typography variant="body2" gutterBottom style={{ marginBottom: 20 }}>
                        {rowCtx ? `Partnumber: ${rowCtx.partNumber}` : ''}
                    </Typography>

                    <QCXTextField
                        label="Quantidade"
                        variant="outlined"
                        fullWidth
                        autoFocus
                        value={qtdeInput}
                        onFocus={handleFocusInput}   
                        onBlur={handleBlurInput}  
                        onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9.,]/g, '');
                            setQtdeInput(raw);
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            const blocked = ['e', 'E', '+', '-'];
                            if (blocked.includes(e.key)) e.preventDefault();
                        }}
                        inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' }}
                        error={!!rowCtx && !qtdeValida}
                        helperText={!!rowCtx && !qtdeValida ? 'Quantidade inválida (maior que o saldo ou negativa).' : ' '}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeQtdeDialog}>Cancelar</Button>
                    <Button color="secondary" variant="contained" onClick={handleSalvarQtde} disabled={!qtdeValida}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );

}